import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, Buildings, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";

function DistrictForm({ onSubmit, onCancel }) {
  const [f, setF] = useState({ nome: "", gestore: "", lunghezza_rete_km: 10, center_lat: 40.75, center_lng: 14.64 });
  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      ...f,
      lunghezza_rete_km: parseFloat(f.lunghezza_rete_km),
      center_lat: parseFloat(f.center_lat),
      center_lng: parseFloat(f.center_lng),
    });
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-400">Nome distretto</Label>
        <Input required value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" data-testid="new-dist-nome" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-400">Gestore</Label>
        <Input required value={f.gestore} onChange={(e) => setF({ ...f, gestore: e.target.value })} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" data-testid="new-dist-gestore" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-400">Lunghezza rete (km)</Label>
        <Input type="number" step="0.1" required value={f.lunghezza_rete_km} onChange={(e) => setF({ ...f, lunghezza_rete_km: e.target.value })} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" data-testid="new-dist-km" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-400">Lat centro</Label>
          <Input type="number" step="0.0001" value={f.center_lat} onChange={(e) => setF({ ...f, center_lat: e.target.value })} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-400">Lng centro</Label>
          <Input type="number" step="0.0001" value={f.center_lng} onChange={(e) => setF({ ...f, center_lng: e.target.value })} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" type="button" onClick={onCancel} className="border-slate-700">Annulla</Button>
        <Button type="submit" className="bg-[#3EB6F0] text-[#070B14] hover:bg-[#2CA4DE] gap-2" data-testid="new-dist-save">
          <FloppyDisk weight="bold" size={14} /> Crea
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function DistrictsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/v1/districts");
      setItems(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Errore caricamento")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (form) => {
    try {
      await api.post("/v1/admin/districts", form);
      toast.success("Distretto creato");
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Errore")); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare il distretto?")) return;
    try { await api.delete(`/v1/admin/districts/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Errore")); }
  };

  if (loading) return <div className="text-xs text-slate-500 font-mono text-center py-8">Caricamento...</div>;
  return (
    <div className="bg-[#111827] border border-slate-800 rounded-lg overflow-hidden" data-testid="districts-panel">
      <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Buildings size={16} weight="duotone" className="text-[#3EB6F0]" />
          <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Distretti registrati · {items.length}</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#3EB6F0] text-[#070B14] hover:bg-[#2CA4DE] gap-2" data-testid="add-district-button">
              <Plus weight="bold" size={14} /> Nuovo distretto
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
            <DialogHeader><DialogTitle className="font-heading">Nuovo distretto</DialogTitle></DialogHeader>
            <DistrictForm onSubmit={create} onCancel={() => setOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>
      <table className="w-full text-xs">
        <thead className="text-slate-500 uppercase tracking-widest">
          <tr className="border-b border-slate-800">
            <th className="text-left px-4 py-2 font-mono">ID</th>
            <th className="text-left px-4 py-2 font-mono">Nome</th>
            <th className="text-left px-2 py-2 font-mono">Gestore</th>
            <th className="text-right px-2 py-2 font-mono">Rete km</th>
            <th className="text-center px-3 py-2 font-mono">Azioni</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {items.map((d) => (
            <tr key={d.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`district-row-${d.id}`}>
              <td className="px-4 py-2 text-slate-300">{d.id}</td>
              <td className="px-4 py-2 text-slate-200 font-semibold">{d.nome}</td>
              <td className="px-2 py-2 text-slate-400">{d.gestore}</td>
              <td className="px-2 py-2 text-right text-slate-200">{d.lunghezza_rete_km}</td>
              <td className="px-3 py-2 text-center">
                {d.id === "district-nocera-nord" ? (
                  <span className="text-[10px] text-slate-600">principale</span>
                ) : (
                  <button className="text-slate-500 hover:text-[#EF4444]" onClick={() => del(d.id)} data-testid={`delete-district-${d.id}`}>
                    <Trash size={14} weight="bold" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
